/**
 * useSpeechToText hook - Voice input with backend transcription.
 *
 * Records audio using the browser's MediaRecorder API and sends it to
 * the backend for transcription. This keeps API credentials secure on
 * the server rather than exposing them in the browser.
 *
 * Note: This hook has similar state properties and method names to the previous
 * Azure Speech SDK version, but signatures have evolved (e.g., stopListening is
 * now async) and new features added (isTranscribing state, options parameter).
 *
 * @security This implementation addresses issue #75 by removing
 * VITE_AZURE_SPEECH_KEY from the frontend entirely.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useAudioRecorder } from './useAudioRecorder'

/**
 * Map raw API/network errors to user-friendly messages.
 */
function toUserFriendlyError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err)
  const lower = message.toLowerCase()

  // Network / timeout
  if (lower.includes('timeout') || lower.includes('timed out') || lower.includes('econnaborted')) {
    return 'Transcription timed out. Please try again.'
  }
  if (lower.includes('network') || lower.includes('failed to fetch') || lower.includes('err_internet')) {
    return 'Network error. Please check your connection and try again.'
  }

  // Backend returned a recognisable detail message — map to user-friendly equivalent
  if (lower.includes('recording too short') || lower.includes('hold the microphone')) {
    return 'Recording too short. Please hold longer while speaking.'
  }
  if (lower.includes('not configured') || lower.includes('unavailable')) {
    return 'Voice input is temporarily unavailable. Please try text input.'
  }
  if (lower.includes('busy') || lower.includes('try again in a moment')) {
    return 'Transcription service is busy. Please try again in a moment.'
  }
  if (lower.includes('audio format') || lower.includes('unsupported')) {
    return 'Audio format not supported. Please try a different browser.'
  }

  // Generic fallback
  return 'Transcription failed. Please try again.'
}

/**
 * Transcription function type
 * Takes audio blob, returns transcribed text
 */
export type TranscribeFunction = (audio: Blob) => Promise<{ text: string; confidence: number }>

/**
 * Speech recognition state
 */
export interface SpeechToTextState {
  isListening: boolean
  transcript: string
  interimTranscript: string
  error: string | null
  isSupported: boolean
  /** Whether transcription is in progress (audio recorded, waiting for backend) */
  isTranscribing: boolean
}

/**
 * Speech recognition actions
 */
export interface SpeechToTextActions {
  startListening: () => Promise<void>
  stopListening: () => Promise<void>
  /** Cancel recording without transcribing (for short recordings) */
  cancelListening: () => void
  resetTranscript: () => void
}

/**
 * Hook return type
 */
export interface UseSpeechToTextReturn extends SpeechToTextState, SpeechToTextActions {}

/**
 * Hook configuration options
 */
export interface UseSpeechToTextOptions {
  /**
   * Function to transcribe audio blob via backend API.
   * Required for voice input to work - if not provided, the hook will
   * record audio but display an error when stopping.
   */
  transcribe?: TranscribeFunction
  /**
   * Called when transcription completes successfully
   */
  onTranscriptionComplete?: (text: string, confidence: number) => void
  /**
   * Called when transcription fails
   */
  onTranscriptionError?: (error: Error) => void
}

/**
 * useSpeechToText hook
 *
 * Provides speech-to-text functionality by recording audio in the browser
 * and sending it to the backend for transcription.
 *
 * @example
 * ```tsx
 * const { isListening, transcript, startListening, stopListening } = useSpeechToText({
 *   transcribe: async (blob) => {
 *     const response = await apiClient.transcribeAudio(sessionId, blob)
 *     return response
 *   }
 * })
 * ```
 */
export function useSpeechToText(options: UseSpeechToTextOptions = {}): UseSpeechToTextReturn {
  const { transcribe, onTranscriptionComplete, onTranscriptionError } = options

  const [transcript, setTranscript] = useState('')
  const [interimTranscript, setInterimTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isTranscribing, setIsTranscribing] = useState(false)

  // Use the audio recorder hook
  const {
    isRecording,
    error: recorderError,
    isSupported,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useAudioRecorder()

  // Track whether we're currently listening (alias for isRecording for API compatibility)
  const isListening = isRecording

  // Store the latest options in refs to avoid stale closures
  const transcribeRef = useRef(transcribe)
  const onCompleteRef = useRef(onTranscriptionComplete)
  const onErrorRef = useRef(onTranscriptionError)

  useEffect(() => {
    transcribeRef.current = transcribe
    onCompleteRef.current = onTranscriptionComplete
    onErrorRef.current = onTranscriptionError
  }, [transcribe, onTranscriptionComplete, onTranscriptionError])

  // Sync recorder error to our error state
  useEffect(() => {
    if (recorderError) {
      setError(recorderError)
    }
  }, [recorderError])

  /**
   * Start listening (recording audio)
   */
  const startListening = useCallback(async () => {
    setError(null)
    setInterimTranscript('')

    try {
      await startRecording()
    } catch (err) {
      console.error('[useSpeechToText] Failed to start recording:', err)
      setError(err instanceof Error ? err.message : 'Failed to start recording')
      setInterimTranscript('')
    }
  }, [startRecording])

  /**
   * Stop listening and transcribe the recording
   */
  const stopListening = useCallback(async () => {
    setInterimTranscript('')

    try {
      const audioBlob = await stopRecording()

      if (!audioBlob || audioBlob.size === 0) {
        console.error('[useSpeechToText] No audio recorded')
        const errorMessage = 'No audio was captured. Please try again.'
        setError(errorMessage)
        onErrorRef.current?.(new Error('No audio captured - recording may have failed'))
        return
      }

      // If no transcribe function provided, show error to user
      if (!transcribeRef.current) {
        console.error('[useSpeechToText] No transcribe function provided - voice input not configured')
        const errorMessage = 'Voice input is not configured for this session.'
        setError(errorMessage)
        onErrorRef.current?.(new Error('Transcription function not provided - session configuration error'))
        return
      }

      // Send to backend for transcription
      // Clear interim transcript - the TextInput shows "Transcribing..." status separately
      setIsTranscribing(true)
      setInterimTranscript('')

      try {
        const result = await transcribeRef.current(audioBlob)

        if (result.text) {
          setTranscript((prev) => {
            // Append to existing transcript with space if needed
            const separator = prev && !prev.endsWith(' ') ? ' ' : ''
            return prev + separator + result.text
          })

          onCompleteRef.current?.(result.text, result.confidence)
        } else {
          // Backend returned empty transcription - audio was unclear or too short
          console.error('[useSpeechToText] Empty transcription result')
          const errorMessage = 'Could not understand audio. Please speak clearly and try again.'
          setError(errorMessage)
          onErrorRef.current?.(new Error(errorMessage))
        }
      } catch (transcribeErr) {
        console.error('[useSpeechToText] Transcription failed:', transcribeErr)
        const errorMessage = toUserFriendlyError(transcribeErr)
        setError(errorMessage)
        onErrorRef.current?.(transcribeErr instanceof Error ? transcribeErr : new Error(errorMessage))
      } finally {
        setIsTranscribing(false)
        setInterimTranscript('')
      }
    } catch (err) {
      console.error('[useSpeechToText] Failed to stop recording:', err)
      setError(err instanceof Error ? err.message : 'Failed to stop recording')
      setIsTranscribing(false)
      setInterimTranscript('')
    }
  }, [stopRecording])

  /**
   * Reset transcript
   */
  const resetTranscript = useCallback(() => {
    setTranscript('')
    setInterimTranscript('')
    setError(null)
  }, [])

  /**
   * Cancel recording without transcribing
   * Used for short recordings that would fail anyway
   */
  const cancelListening = useCallback(() => {
    cancelRecording()
    setInterimTranscript('')
  }, [cancelRecording])

  // Cleanup on unmount - cancel any recording in progress
  useEffect(() => {
    return () => {
      cancelRecording()
    }
  }, [cancelRecording])

  return {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    isTranscribing,
    startListening,
    stopListening,
    cancelListening,
    resetTranscript,
  }
}

export default useSpeechToText
